import { supabase } from './supabase'
import type { CampaignKind } from '../types'

/**
 * The menu as a customer sees it, without a session.
 *
 * Everything comes from one `security definer` function (supabase/qr-menu.sql)
 * rather than from the tables: the shape returned there is written down, so a
 * column added to `products` later cannot quietly become public.
 */

export interface PublicProduct {
  id: string
  name: string
  priceKurus: number
  description: string | null
  soldOut: boolean
  /** Kiloyla satılıyor: fiyat 1 kg'ın. */
  byWeight?: boolean
  /** Porsiyonlar (3 Kas 2026): ürünün kendi fiyatı 1 porsiyon, bunlar ek. */
  portions?: { name: string; priceKurus: number }[] | null
  /** Ürün fotoğrafı; Görselli menü modülü kapalıysa ya da fotoğraf yoksa null. */
  image?: string | null
}

export interface PublicCategory {
  id: string
  name: string
  sortOrder: number
  products: PublicProduct[]
}

/** Patronun Ayarlar › QR Menü'de seçtiği görünüm. Boş = varsayılan. */
export interface MenuStyle {
  /** Vurgu rengi, `#rrggbb`. */
  accent?: string
  /** Kurum adının altındaki kısa yazı; boşsa "Menü". */
  tagline?: string
  /** Ürün açıklamaları görünsün mü; varsayılan evet. */
  descriptions?: boolean
  theme?: 'auto' | 'light' | 'dark'
  /** İşletmenin Google yorum bağlantısı; boşsa menüde değerlendirme düğmesi yok. */
  reviewUrl?: string
}

export const DEFAULT_ACCENT = '#ff5a1f'

/** Google yer kimliği: ChIJ… gibi, yalnız harf, rakam, - ve _. */
const PLACE_ID = /^[A-Za-z0-9_-]{20,}$/

/** Kimliğin bulunduğu Google sayfası (Ayarlar'daki açıklamada bağlantı). */
export const PLACE_ID_FINDER =
  'https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder'

/** Google'ın yorum bağlantılarının geldiği adresler (Yorum iste, Haritalar, kısa link). */
const REVIEW_HOSTS = /(^|\.)(google\.[a-z.]+|g\.page|goo\.gl|g\.co)$/i

/**
 * Bağlantı Google'a mı gidiyor: ayarda kaydetmeden önce, menüde de
 * göstermeden önce bakılıyor (menu_style'a elle başka bir şey yazılmış olsa
 * bile menü yabancı bir adrese düğme koymasın).
 */
export function reviewLink(url: string | undefined): string | undefined {
  const s = url?.trim()
  if (!s) return undefined
  // Yalnız Google yer kimliği (ChIJ…) yapıştırıldıysa (1 Kas 2026, kullanıcı):
  // doğrudan yıldız verme penceresini açan bağlantı kuruluyor. İşletme
  // Profili'ne erişimi olmayan da kimliği Place ID Finder'dan alabiliyor.
  if (PLACE_ID.test(s)) return `https://search.google.com/local/writereview?placeid=${s}`
  try {
    const u = new URL(s)
    return u.protocol === 'https:' && REVIEW_HOSTS.test(u.hostname) ? u.href : undefined
  } catch {
    return undefined
  }
}

/**
 * Çeviriler, dil başına: anahtar `p:<ürün id>`, `c:<kategori id>` ya da
 * `t:tagline`. Yalnızca Türkçesi değişmemiş olanlar geliyor; olmayan kalem
 * Türkçe görünüyor.
 */
export type MenuTranslations = Partial<Record<string, Record<string, { n: string; d?: string }>>>

/**
 * QR menüde gösterilen kampanya (supabase/qr-kampanya.sql): açık, patronun
 * "QR menüde göster" dediği ve süresi geçmemiş olanlar. Gün ve saat burada
 * süzülüyor (isCampaignLive), kasayla aynı hesap.
 */
export interface PublicCampaign {
  id: string
  name: string
  kind: CampaignKind
  percent?: number
  amountKurus?: number
  minTotalKurus?: number
  buyQty?: number
  payQty?: number
  categoryIds: string[]
  productIds: string[]
  days: number[]
  startTime?: string
  endTime?: string
  startsOn?: string
  endsOn?: string
  /** Patronun afişi; Görselli menü kapalıysa ya da yoksa yok, stok fotoğraf konuyor. */
  image?: string | null
}

export interface PublicMenu {
  name: string
  style: MenuStyle
  categories: PublicCategory[]
  translations: MenuTranslations
  campaigns: PublicCampaign[]
}

/**
 * Null means "there is nothing to show here", no such institution, or one
 * that has not switched the QR menu on. The two are deliberately the same
 * answer: which institutions exist is not something a business that has kept
 * its menu closed owes to a passer-by.
 */
export async function fetchPublicMenu(tenantId: string): Promise<PublicMenu | null> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const { data, error } = await supabase.rpc('public_menu', { t: tenantId })
  if (error) throw new Error(error.message)
  if (!data) return null

  const menu = data as PublicMenu
  return {
    name: menu.name ?? '',
    style: menu.style ?? {},
    translations: menu.translations ?? {},
    campaigns: (menu.campaigns ?? []).map((c) => ({
      ...c,
      categoryIds: c.categoryIds ?? [],
      productIds: c.productIds ?? [],
      days: c.days ?? [],
    })),
    categories: (menu.categories ?? []).map((c) => ({
      ...c,
      products: c.products ?? [],
    })),
  }
}
